import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import { UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER } from 'common/utility/userRoles';

import { SR_API_ACTION_UTILITY_METABASE_CHART_GET_BY_PARTNER_ID } from 'api/utilityActionTypes';

import UtilityPartnerActions from 'client/actions/UtilityPartner';
import UtilityMetabaseChartsActions from 'client/actions/UtilityMetabaseCharts';

import { SCREEN_ID_UTILITY_PROFIT_PARTNER_REPORTS } from 'client/constants/screenIds';

import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withScreenId from 'client/helpers/withScreenId';
import { withUtilityApiState } from 'client/helpers/withApiState';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';

import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import UPMetabaseCharts from 'client/components/UPMetabaseCharts/UPMetabaseCharts';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER],
  redirectUrl: '/utility-profit/login',
  addRedirectQueryParam: true,
})
@branch(({ match }) => ({
  user: ['utilityUser'],
  partner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
}))
@branch(({ partner }) => {
  if (!partner) {
    return {};
  }
  return {
    metabaseCharts: ['utilityMetabaseCharts', 'byPartnerId', partner.id],
  };
})
@withUtilityApiState({
  getMetabaseChartsApiState: {
    action: SR_API_ACTION_UTILITY_METABASE_CHART_GET_BY_PARTNER_ID,
  },
})
@withScreenId(SCREEN_ID_UTILITY_PROFIT_PARTNER_REPORTS)
@withScrollToTop
class UtilityPartnerReportsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Internet Amenity | Utility Profit',
  });

  static propTypes = {
    partner: PropTypes.object,
    user: PropTypes.object,
    metabaseCharts: PropTypes.array,
    getMetabaseChartsApiState: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    return UtilityPartnerActions.getBySlug(inject, {
      slug: match.params.partnerSlug,
    }).then(() => {
      const partner = inject.state.get(['utilityPartners', 'bySlug', match.params.partnerSlug]);
      if (partner) {
        return UtilityMetabaseChartsActions.getByPartnerId(inject, {
          partnerId: partner.id,
        });
      }
    });
  }

  render() {
    const { user, partner, metabaseCharts, getMetabaseChartsApiState, dispatch } = this.props;

    if (!partner || getMetabaseChartsApiState.inProgress) {
      return <div className="loading-gif" />;
    }

    if (Array.isArray(metabaseCharts) && metabaseCharts.length === 0) {
      return <Redirect to="/utility-profit" />;
    }

    const navbarLinks = lodashCompact([
      {
        label: 'Home',
        value: `/utility-profit/${partner.slug}`,
      },
      {
        label: 'Internet Amenity',
        value: `/utility-profit/${partner.slug}/internet-amenity`,
        icon:
          new Date() > new Date('2025-02-18')
            ? null
            : {
                url: '/images/new-feature-yellow.svg',
                width: 74,
                height: 17,
                offset: {
                  top: -12,
                  right: -70,
                },
              },
      },
    ]);

    return (
      <SrPartnerPageV2
        navbarLinks={navbarLinks}
        navbarTitle={partner.name}
        navbarImage={partner.logoUrl}
        utilityUser={user}
        utilityPartner={partner}
        padding="0"
        contentPadding="50px 0"
        maxWidth="1140px"
        navbarMaxWidth="1180px"
        redirectToLastLocation={false}
      >
        <SrBox padding="40px 0 30px">
          <SrFlex columnGap="15px" alignItems="baseline">
            <SrText font="greycliff" weight="700" size="28">
              Internet Amenity
            </SrText>
          </SrFlex>
        </SrBox>
        <UPMetabaseCharts charts={metabaseCharts} dispatch={dispatch} />
      </SrPartnerPageV2>
    );
  }
}

UtilityPartnerReportsPage.displayName = 'UtilityPartnerReportsPage';

export default UtilityPartnerReportsPage;
